import { Text } from '@chakra-ui/react';
import { useEffect } from 'react';
import type { GroupBase, SingleValue } from 'react-select';

import { Checkbox, Select as RevelioSelect } from '@revelio/ui';

import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type { PrimaryFilterConfig } from '../PageConfigProvider/page-reducer';
import type {
  SecondaryDimensionOption,
  SecondaryDimensionValues,
} from '../PageConfigProvider/types/dimension';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import styles from './secondary-dimension-selector.module.css';
import labelStyles from './section-label.module.css';
import { getAvailableSegments } from './utils/segment-mapping';

type SecondaryDimensionSelectorProps = {
  item: PositionedItem;
  primaryFilter: PrimaryFilterConfig;
  forceSegment?: boolean;
  isDisabled?: boolean;
  showGroupBySegment?: boolean;
  label?: string;
  excludeDimensions?: SecondaryDimensionValues[];
};

export const SecondaryDimensionSelector = ({
  item,
  primaryFilter,
  forceSegment = false,
  isDisabled = false,
  showGroupBySegment = false,
  label = 'Segment',
  excludeDimensions,
}: SecondaryDimensionSelectorProps) => {
  const { dispatch } = useTabContentConfig();
  const id = item?.id;

  const metric = item?.plot?.defaultMetricOptions?.[0];

  const { secondaryDimension, groupBySegment, shareOfSegment } =
    item?.plot || {};
  const primaryFilterId = primaryFilter?.id;
  const metrics = item?.plot?.defaultMetricOptions;

  const hiddenDimensions: SecondaryDimensionValues[] = excludeDimensions ?? [];
  const availableSecondaryDimensions = getAvailableSegments({
    chartType: item?.plot?.chartType,
    primaryFilterId,
    metrics,
    activitiesEnabled: true,
  }).filter((dim) => !hiddenDimensions.includes(dim.value));

  const selectedSecondaryDimension =
    availableSecondaryDimensions.find(
      (dimension) => dimension.value === secondaryDimension
    ) ?? (forceSegment ? availableSecondaryDimensions?.[0] : null);

  useEffect(() => {
    if (
      (selectedSecondaryDimension === null ||
        selectedSecondaryDimension?.value === null ||
        !availableSecondaryDimensions.length) &&
      secondaryDimension !== undefined
    ) {
      dispatch({
        type: 'UPDATE_ITEM_SECONDARY_DIMENSION',
        payload: { id, secondaryDimension: undefined },
        replacePreviousHistorySnapshot: true,
      });
    }

    if (
      selectedSecondaryDimension &&
      secondaryDimension !== selectedSecondaryDimension.value
    ) {
      dispatch({
        type: 'UPDATE_ITEM_SECONDARY_DIMENSION',
        payload: { id, secondaryDimension: selectedSecondaryDimension.value },
      });
    }
  }, [
    availableSecondaryDimensions,
    id,
    dispatch,
    selectedSecondaryDimension,
    secondaryDimension,
  ]);

  const onChangeSecondaryDimension = (
    secondaryDimension: SingleValue<SecondaryDimensionOption>
  ) => {
    if (!secondaryDimension) {
      dispatch({
        type: 'UPDATE_ITEM_SECONDARY_DIMENSION',
        payload: { id, secondaryDimension: undefined },
      });
      return;
    }

    if (secondaryDimension.value === null) {
      dispatch({
        type: 'UPDATE_ITEM_SECONDARY_DIMENSION',
        payload: { id, secondaryDimension: undefined },
      });
      return;
    }

    dispatch({
      type: 'UPDATE_ITEM_SECONDARY_DIMENSION',
      payload: { id, secondaryDimension: secondaryDimension.value },
    });
  };

  const onChangeGroupBySegment = (groupBySegment: boolean) => {
    dispatch({
      type: 'UPDATE_ITEM_GROUP_BY_SEGMENT',
      payload: { id, groupBySegment },
    });
  };

  const onChangeShareOfSegment = (shareOfSegment: boolean) => {
    dispatch({
      type: 'UPDATE_ITEM_SHARE_OF_SEGMENT',
      payload: { id, shareOfSegment },
    });
  };

  if (availableSecondaryDimensions.length === 0) return null;

  return (
    <div className={styles.section}>
      <Text
        className={labelStyles.sectionLabel}
        fontSize="sm"
        fontWeight={600}
        marginBottom="0.5rem"
        opacity={isDisabled ? 0.6 : 1}
      >
        {label}
      </Text>
      <RevelioSelect<
        SecondaryDimensionOption,
        false,
        GroupBase<SecondaryDimensionOption>
      >
        options={availableSecondaryDimensions}
        value={selectedSecondaryDimension}
        onChange={onChangeSecondaryDimension}
        isClearable={!forceSegment}
        isSearchable
        isDisabled={isDisabled}
        size="sm"
      />
      {showGroupBySegment && (
        <Checkbox
          className={styles.checkbox}
          labelSize="sm"
          checked={groupBySegment}
          onChange={(e) => onChangeGroupBySegment(e.target.checked)}
          disabled={isDisabled}
        >
          Group by Segment
        </Checkbox>
      )}
      {item.plot?.chartType === 'line' &&
        secondaryDimension &&
        metric === 'headcount' && (
          <Checkbox
            className={styles.checkbox}
            labelSize="sm"
            checked={shareOfSegment ?? false}
            onChange={(e) => onChangeShareOfSegment(e.target.checked)}
            disabled={isDisabled}
          >
            Share of Segment
          </Checkbox>
        )}
    </div>
  );
};
